/**
 * Componentes y utilidades compartidas por las páginas.
 */
import { useState, type ReactNode } from 'react';
import { Badge, Button, Group, NumberInput, Stack, Text, Textarea, type NumberInputProps } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  CONCEPTOS_GASTO,
  CONCEPTOS_INGRESO,
  FORMAS_PAGO,
  formatearEuros,
  type ElementoCatalogo,
} from '@gestionpyme/dominio';
import { ErrorApi } from './api';

// ── Importes ────────────────────────────────────────────────────────────────

export const euros = (c: number | null | undefined) => (c === null || c === undefined ? '—' : formatearEuros(c));

/** Campo de importe en euros que trabaja internamente en céntimos. */
export function CampoEuros({
  valor,
  onCambio,
  ...props
}: { valor: number | null; onCambio: (centimos: number | null) => void } & Omit<NumberInputProps, 'value' | 'onChange'>) {
  return (
    <NumberInput
      decimalSeparator=","
      thousandSeparator="."
      decimalScale={2}
      fixedDecimalScale
      suffix=" €"
      hideControls
      inputMode="decimal"
      value={valor === null ? '' : valor / 100}
      onChange={(v) => {
        if (v === '' || v === null) onCambio(null);
        else onCambio(Math.round(Number(v) * 100));
      }}
      {...props}
    />
  );
}

/** Campo de porcentaje (7 = 7 %) que trabaja en puntos básicos (700). */
export function CampoPorcentaje({
  valor,
  onCambio,
  ...props
}: { valor: number | null; onCambio: (pb: number | null) => void } & Omit<NumberInputProps, 'value' | 'onChange'>) {
  return (
    <NumberInput
      decimalSeparator=","
      decimalScale={2}
      suffix=" %"
      min={0}
      max={100}
      hideControls
      inputMode="decimal"
      value={valor === null ? '' : valor / 100}
      onChange={(v) => onCambio(v === '' || v === null ? null : Math.round(Number(v) * 100))}
      {...props}
    />
  );
}

// ── Catálogos para selectores ───────────────────────────────────────────────

const opciones = (lista: ElementoCatalogo[], conCodigo = true) =>
  lista.map((e) => ({ value: e.codigo, label: conCodigo ? `${e.codigo} · ${e.nombre}` : e.nombre }));

export const OPCIONES_CONCEPTO_INGRESO = opciones(CONCEPTOS_INGRESO.lista);
export const OPCIONES_CONCEPTO_GASTO = opciones(CONCEPTOS_GASTO.lista);
export const OPCIONES_FORMA_PAGO = opciones(FORMAS_PAGO.lista, false);
export const OPCIONES_IGIC = [
  { value: '0', label: '0 % (exento / sin IGIC)' },
  { value: '300', label: '3 % (reducido)' },
  { value: '700', label: '7 % (general)' },
  { value: '950', label: '9,5 % (incrementado)' },
  { value: '1500', label: '15 % (especial)' },
];

export const nombreFormaPago = (c: string | null) => (c ? (FORMAS_PAGO.nombre(c) ?? c) : '—');

export const ESTADOS: Record<string, { texto: string; color: string }> = {
  activo: { texto: 'Activo', color: 'teal' },
  pendiente_justificar: { texto: 'Pendiente de justificar', color: 'orange' },
  anulado: { texto: 'Anulado', color: 'gray' },
};

export function EstadoMovimiento({ estado }: { estado: string }) {
  const e = ESTADOS[estado] ?? { texto: estado, color: 'gray' };
  return (
    <Badge color={e.color} variant="light" size="sm">
      {e.texto}
    </Badge>
  );
}

// ── Fechas y ejercicio ──────────────────────────────────────────────────────

export const fechaCorta = (iso: string | null | undefined) => (iso ? iso.split('-').reverse().join('/') : '—');

export const EJERCICIO_ACTUAL = new Date().getFullYear();
export const OPCIONES_EJERCICIO = [EJERCICIO_ACTUAL + 1, EJERCICIO_ACTUAL, EJERCICIO_ACTUAL - 1, EJERCICIO_ACTUAL - 2].map(String);
export const TRIMESTRE_ACTUAL = Math.floor(new Date().getMonth() / 3) + 1;

// ── Notificaciones ──────────────────────────────────────────────────────────

export function avisarError(e: unknown) {
  notifications.show({ color: 'red', title: 'No se ha podido completar', message: e instanceof Error ? e.message : String(e), autoClose: 8000 });
}

export function avisarOk(mensaje: string, titulo = 'Hecho') {
  notifications.show({ color: 'teal', title: titulo, message: mensaje });
}

export function mostrarAvisos(avisos: (string | null | undefined)[] | undefined) {
  for (const a of avisos ?? []) if (a) notifications.show({ color: 'yellow', title: 'Revisa', message: a, autoClose: 12000 });
}

/**
 * Ejecuta una acción y, si el periodo ya está declarado, pide el motivo de la
 * rectificación y la reintenta con él.
 */
export async function conRectificacion<T>(accion: (rectificacion?: { motivo: string }) => Promise<T>): Promise<T | undefined> {
  try {
    return await accion();
  } catch (e) {
    if (!(e instanceof ErrorApi) || e.codigo !== 'PERIODO_DECLARADO') throw e;
    const motivo = await pedirMotivo(e.message);
    if (!motivo) return undefined;
    return accion({ motivo });
  }
}

function pedirMotivo(mensaje: string): Promise<string | null> {
  return new Promise((resolver) => {
    const id = modals.open({
      title: 'Periodo ya declarado',
      onClose: () => resolver(null),
      children: <FormularioMotivo mensaje={mensaje} onAceptar={(m) => { resolver(m); modals.close(id); }} onCancelar={() => modals.close(id)} />,
    });
  });
}

function FormularioMotivo({ mensaje, onAceptar, onCancelar }: { mensaje: string; onAceptar: (m: string) => void; onCancelar: () => void }) {
  const [motivo, setMotivo] = useState('');
  return (
    <Stack>
      <Text size="sm">{mensaje}</Text>
      <Text size="sm" c="dimmed">
        El cambio quedará registrado y la declaración se marcará para revisar (puede requerir una complementaria).
      </Text>
      <Textarea label="Motivo de la rectificación" value={motivo} onChange={(e) => setMotivo(e.currentTarget.value)} minRows={2} autoFocus />
      <Group justify="flex-end">
        <Button variant="default" onClick={onCancelar}>
          Cancelar
        </Button>
        <Button disabled={motivo.trim().length < 5} onClick={() => onAceptar(motivo.trim())}>
          Rectificar
        </Button>
      </Group>
    </Stack>
  );
}

// ── Diseño ──────────────────────────────────────────────────────────────────

export function Cabecera({ titulo, descripcion, children }: { titulo: string; descripcion?: string; children?: ReactNode }) {
  return (
    <Group justify="space-between" align="flex-end" mb="md" wrap="wrap" gap="sm">
      <div>
        <Text fw={700} fz={24} lh={1.2}>
          {titulo}
        </Text>
        {descripcion && (
          <Text c="dimmed" size="sm">
            {descripcion}
          </Text>
        )}
      </div>
      {children && <Group gap="xs">{children}</Group>}
    </Group>
  );
}
